// Receiving a quantity that has to be worked out from individual pieces of varying size — granite,
// marble, glass and plywood by area; pipes, sections and timber cut to length; stone blocks by volume.
// The material's own unit decides how many dimensions a piece needs; nothing here is stone-specific.

export type DimensionUnit = 'in' | 'ft' | 'cm' | 'm';
export const DIMENSION_UNITS: DimensionUnit[] = ['in', 'ft', 'cm', 'm'];
export const DIMENSION_UNIT_LABEL: Record<DimensionUnit, string> = { in: 'Inches', ft: 'Feet', cm: 'Centimeters', m: 'Meters' };
const TO_METERS: Record<DimensionUnit, number> = { in: 0.0254, ft: 0.3048, cm: 0.01, m: 1 };
const M_TO_SQFT = 10.763910417;
const M_TO_FT = 3.280839895;

const normalizeUnitKey = (value: string) => String(value || '').trim().toLowerCase().replace(/[.\s_-]+/g, '');

/** How many dimensions (length, area, volume) a unit is measured by; 0 when the unit isn't size-based. */
export function pieceShapeForUnit(unit: string): 0 | 1 | 2 | 3 {
  const key = normalizeUnitKey(unit);
  if (key === 'rft' || key === 'meter' || key === 'meters' || key === 'mtr') return 1;
  if (key === 'sft' || key === 'sqft' || key === 'sqm' || key === 'sqmt' || key === 'sqmeter' || key === 'sqmeters') return 2;
  if (key === 'cum' || key === 'cbm') return 3;
  return 0;
}

/** Label for each dimension box, in order: Length, then Width, then Height. */
export const DIMENSION_LABELS: Record<0 | 1 | 2 | 3, string[]> = { 0: [], 1: ['Length'], 2: ['Length', 'Width'], 3: ['Length', 'Width', 'Height'] };

/** The dimension unit a material's own unit is conventionally measured in, until changed for a delivery. */
export function defaultDimensionUnitFor(unit: string): DimensionUnit {
  const key = normalizeUnitKey(unit);
  if (key === 'sft' || key === 'sqft') return 'in'; // granite/marble slabs: inches
  if (key === 'rft') return 'ft'; // cut lengths: feet
  return 'm'; // Sqm, Cum, Meter
}

const targetFactorFromMetric = (unit: string): number => {
  const key = normalizeUnitKey(unit);
  if (key === 'sft' || key === 'sqft') return M_TO_SQFT;
  if (key === 'rft') return M_TO_FT;
  return 1; // Sqm, Cum, Meter are already the metric figure
};

const round3 = (value: number) => Math.round((value + Number.EPSILON) * 1000) / 1000;

/** One piece's contribution to the material's own unit (not multiplied by how many pieces). 0 if incomplete. */
export function pieceRowQuantity(dims: number[], dimensionUnit: DimensionUnit, targetUnit: string): number {
  const shape = pieceShapeForUnit(targetUnit);
  if (!shape) return 0;
  const factor = TO_METERS[dimensionUnit];
  const valuesM = dims.slice(0, shape).map((value) => (value > 0 ? value * factor : 0));
  if (valuesM.length < shape || valuesM.some((value) => value <= 0)) return 0;
  const baseMetric = valuesM.reduce((product, value) => product * value, 1);
  return round3(baseMetric * targetFactorFromMetric(targetUnit));
}

export type PieceRow = { dims: string[]; count: string };
export const emptyPieceRow = (shape: 0 | 1 | 2 | 3): PieceRow => ({ dims: Array.from({ length: shape }, () => ''), count: '1' });

const toNum = (value: string) => { const n = parseFloat(value); return Number.isFinite(n) ? n : 0; };

/** A row's total (its per-piece quantity times how many pieces), 0 if the row is incomplete. */
export function pieceRowTotal(row: PieceRow, dimensionUnit: DimensionUnit, targetUnit: string): number {
  const count = toNum(row.count);
  if (count <= 0) return 0;
  return round3(pieceRowQuantity(row.dims.map(toNum), dimensionUnit, targetUnit) * count);
}

/** The delivery's total quantity: every complete row's total, added up. */
export function totalPieceQuantity(rows: PieceRow[], dimensionUnit: DimensionUnit, targetUnit: string): number {
  return round3(rows.reduce((sum, row) => sum + pieceRowTotal(row, dimensionUnit, targetUnit), 0));
}
